import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Controller, useFormContext } from 'react-hook-form';
import { Range, getTrackBackground } from 'react-range';
import colors from 'themeColors';
import HelpPopup from 'components/HelpPopup';
import useErrorOrHelpText from './useErrorOrHelpText';

const NumberSliderField = ({
  name,
  label,
  helpText,
  helpPopupContent,
  required = false,
  disabled = false,
  min = 0,
  max = 100,
  step = 1,
  valueLabelSuffix = '',
  validate,
  registerOpts = {},
  formMethods,
}) => {
  const { control, watch } = formMethods || useFormContext();
  const { errorOrHelpText, hasError } = useErrorOrHelpText(name, helpText, formMethods);

  const val = watch(name) || '';

  const wrapperClasses = classNames({
    'z-form-slider-wrap': true,
    // focused: isFocused,
    required,
    'has-val': true,
    invalid: hasError,
  });

  const boundsMessage = (min && max)
    ? `Value must be between ${min} and ${max}.`
    : null;

    const labelDecimalPlaces = step.toString().split('.')[1]?.length || 0;

  return (
    <div className={wrapperClasses}>
      <Controller
        name={name}
        control={control}
        rules={{
          required: { value: required, message: 'This field is required.' },
          min: { value: min, message: boundsMessage || `The minimum value is ${min}.` },
          max: { value: max, message: boundsMessage || `The maximum value is ${max}.` },
          disabled,
          validate,
          valueAsNumber: true,
          ...registerOpts,
        }}
        render={({
          field: { onChange, value, ref },
        }) => {
         return (
            <div className="z-form-slider">
              <Range
                label="Select your value"
                step={step}
                min={min}
                max={max}
                values={[value]}
                onChange={([value]) => onChange(value)}
                renderTrack={({ props, children }) => (
                  <div
                    {...props}
                    className="z-form-slider-track"
                    style={{
                      ...props.style, // eslint-disable-line
                      background: getTrackBackground({
                        values: [value],
                        colors: [colors.primary, colors.gray300],
                        min,
                        max,
                      }),
                    }}
                  >
                    {children}
                  </div>
                )}
                renderThumb={({ props, isDragged }) => (
                  // eslint-disable-next-line
                  <div key={props.key} className="z-form-slider-thumb" {...props}>
                    {isDragged && (
                      <div className="z-form-slider-thumb-label">
                        {value.toFixed(labelDecimalPlaces)}{valueLabelSuffix}
                      </div>
                    )}
                  </div>
                )}
              />
            </div>
          );
        }}
      />

      {label && (
        <label className={classNames('z-form-slider-label', !!disabled && 'z-form-disabled')}>
          {label}
        </label>
      )}
      {!!helpPopupContent && <HelpPopup>{helpPopupContent}</HelpPopup>}
      <div className="z-form-hint-container">
        {errorOrHelpText}
      </div>
    </div>
  );
};

NumberSliderField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  helpText: PropTypes.string,
  helpPopupContent: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
  ]),
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['any'])]),
  valueLabelSuffix: PropTypes.string,
  validate: PropTypes.func,
  // Pass through additional options to react-hook-form `register` method:
  // https://react-hook-form.com/api/useform/register/
  registerOpts: PropTypes.object,
  formMethods: PropTypes.object,
};

export default NumberSliderField;
